import React, { useState, useEffect, useRef } from 'react';
import { Search, X, AlertCircle, ThumbsDown, ThumbsUp, HelpCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/Card';
import { Input } from './components/ui/Input';
import { Button } from './components/ui/Button';
import { useNavigate } from 'react-router-dom';

// COME BACK

const API_KEY = '897c3c2f7b7114d6e8712ca720d280fe';
const BASE_URL = 'https://api.themoviedb.org/3';

const Alert = ({ children }) => (
    <Card className="bg-orange-100 border-orange-400 text-orange-700 mb-4">
      <CardContent className="flex items-center p-4">
        <AlertCircle className="h-5 w-5 mr-2" />
        {children}
      </CardContent>
    </Card>
  );

const MovieDatabase = () => {
  const [recentMovies, setRecentMovies] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [endCredits, setEndCredits] = useState(null);
  const [showEndCredits, setShowEndCredits] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();



  const movieListRef = useRef(null);

  useEffect(() => {
    if (!isLoading) { 
      movieListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage, isLoading]); // Add isLoading as a dependency
  



  useEffect(() => {
    fetchRecentMovies(currentPage);
  }, [currentPage]);

  useEffect(() => {
    let timer;
    if (showAlert) {
      timer = setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [showAlert]);

  const fetchRecentMovies = async (page = 1) => {
    setIsLoading(true); // Loading start
    const response = await fetch(`${BASE_URL}/movie/now_playing?api_key=${API_KEY}&page=${page}`);
    const data = await response.json();
    setRecentMovies(data.results);
    setIsLoading(false); // Loading complete
  };

  const searchMovies = async () => {
    if (searchQuery.trim() === '') return;
    const response = await fetch(`${BASE_URL}/search/movie?api_key=${API_KEY}&query=${searchQuery}`);
    const data = await response.json();
    setSearchResults(data.results);
    setSelectedMovie(null);
    setHasSearched(true);
    setShowAlert(data.results.length === 0);

  };

  const fetchEndCredits = async (movieTitle, movieYear) => {
    try {
      const response = await fetch('/api/endcredits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: movieTitle,
          year: movieYear,
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      // Store as object with summary and confidence
      setEndCredits({
        summary: data.summary,
        confidence: data.confidence || 'unknown'
      });
    } catch (error) {
      console.error('Error fetching end credits:', error);
      setEndCredits({
        summary: 'Unable to fetch end credits information. Please try again later.',
        confidence: 'error'
      });
    }
  };

  const fetchMovieDetails = async (movieId) => {
    const response = await fetch(`${BASE_URL}/movie/${movieId}?api_key=${API_KEY}&append_to_response=credits,videos`);
    const data = await response.json();
    setSelectedMovie(data);
    setEndCredits(null);
    fetchEndCredits(data.title, new Date(data.release_date).getFullYear());
  };

  const handleHomeClick = () => {
    setSelectedMovie(null);
    setSearchResults([]);
    setSearchQuery('');
    setEndCredits(null);
    setCurrentPage(1); // Reset to page 1
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      searchMovies();
    }
  };

  const MovieCard = ({ movie }) => (
    <Card 
      className="w-[calc(50%-1rem)] sm:w-[calc(33.333%-1rem)] md:w-[calc(25%-1rem)] lg:w-[calc(20%-1rem)] m-2 cursor-pointer 
      transform transition duration-300 ease-in-out hover:scale-105 "
      onClick={() => fetchMovieDetails(movie.id)}
    >
      <CardHeader className="p-2">
        <CardTitle className="text-sm">{movie.title}</CardTitle>
        <CardDescription className="text-xs">{movie.release_date || 'Release date TBA'}</CardDescription>
      </CardHeader>
      <CardContent className="p-2">
        <img 
          src={movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : '/images/no-poster.png'} 
          alt={movie.title} 
          className="w-full rounded-md" 
        />
      </CardContent>
      <CardFooter className="p-2">
        <p className="text-xs">
          Movie Score: {movie.vote_average ? movie.vote_average.toFixed(1) : 'N/A'}/10
        </p>
      </CardFooter>
    </Card>
  );

  const LoadingAnimation = () => (
    <div className="flex items-center justify-center">
    <div
      className="w-12 h-12 border-4 rounded-full animate-spin border-t-transparent"
      style={{
        borderColor: 'hsl(30, 100%, 50%)',
        borderTopColor: 'transparent',
      }}
    ></div>
  </div>
  );

  const MovieDetails = ({ movie, endCredits }) => (
    <div className="relative">
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">{movie.title}</CardTitle>
        <p className="text-sm text-gray-500">Released: {movie.release_date}</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        <div>
          <img 
            src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`} 
            alt={movie.title} 
            className="w-full rounded-lg shadow-lg"
          />
        </div>
        
        <div className="space-y-4">
        <MovieDetailsEndCredits endCredits={endCredits} confidence={endCredits?.confidence} />



          <div>
            <h3 className="text-xl font-semibold mb-2">Overview</h3>
            <p>{movie.overview}</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">Details</h3>
            <ul className="space-y-1">
              <li><strong>Genres:</strong> {movie.genres.map(g => g.name).join(', ')}</li>
              <li><strong>Movie Score:</strong> {movie.vote_average.toFixed(1)}/10</li> 
              <li><strong>Runtime:</strong> {movie.runtime} minutes</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">Cast & Crew</h3>
            <ul className="space-y-1">
              <li><strong>Director(s):</strong> {movie.credits.crew.filter(c => c.job === 'Director').map(d => d.name).join(', ')}</li>
              <li><strong>Writer(s):</strong> {movie.credits.crew.filter(c => c.job === 'Writer' || c.job === 'Screenplay').map(w => w.name).join(', ')}</li> 
              <li><strong>Top Cast:</strong> {movie.credits.cast.slice(0, 5).map(c => c.name).join(', ')}</li>
            </ul>
          </div>

          <div> {/* New Box Office Section */}
            <h3 className="text-xl font-semibold mb-2">Box Office</h3>
            <ul className="space-y-1">
                <li><strong>Budget:</strong> {movie.budget === 0 ? 'Not Available' : `$${movie.budget.toLocaleString()}`}</li>
                <li><strong>Gross:</strong> {movie.revenue === 0 ? 'Not Available' : `$${movie.revenue.toLocaleString()}`}</li>
            </ul>
            </div>

          {movie.videos.results.length > 0 && (
            <div>
            <h3 className="text-xl font-semibold mb-2">Trailer</h3>
            <div className="aspect-w-16 aspect-h-9">
                {/* Check if TMDB trailer exists */}
                {movie.videos.results.find(video => video.site === 'YouTube' && video.type === 'Trailer') ? ( 
                <iframe
                    className="w-full h-full rounded-lg"
                    src={`https://www.youtube.com/embed/${movie.videos.results.find(video => video.site === 'YouTube' && video.type === 'Trailer').key}`}
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="movie trailer"
                ></iframe>
                ) : (
                <p>No trailer available from TMDB.</p> 
                )}
            </div>
            </div>
        )}
        

        </div>
      </CardContent>
    </Card>

    <button 
      className="absolute top-2 right-2 p-1 rounded-full bg-gray-800 opacity-70 hover:opacity-100"
      onClick={() => setSelectedMovie(null)} 
    >
      <X className="h-4 w-4 text-white" />
    </button>
  </div>

    
  );

  return (
    <div className="container mx-auto p-2 sm:p-4">
      <div className="flex flex-col items-center mb-4">
        <img 
          src="/images/end-credits-logo.png" 
          alt="End Credits Logo" 
          className="w-full max-w-md h-auto cursor-pointer mb-6 transition-transform duration-300 ease-in-out hover:scale-105"
          onClick={handleHomeClick}
        />

        {/* Summary Text */    
        /* <p className="text-center text-lg sm:text-xl md:text-2xl max-w-2xl mx-auto mb-6 text-white-700 animate-glow">
        Easily find out if a movie has any end-credit surprises. Search for a movie and get instant details about any hidden scenes after the credits roll!
        </p> */}


        <div className="w-full sm:w-2/3 flex space-x-2">
          <Input 
            type="text" 
            placeholder="Search for a movie..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-grow"
          />
          <Button onClick={searchMovies} className="flex-shrink-0">
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
        </div>

        {!selectedMovie && ( 
          <img 
            src="/images/moviePopcorn.png" 
            alt="About" 
            className="w-18 h-36 cursor-pointer mb-4 transition-transform duration-300 ease-in-out hover:scale-110"
            onClick={() => navigate('/about')}
          />
        )}

      </div>

      {selectedMovie ? (
        <MovieDetails movie={selectedMovie} endCredits={endCredits} />
      ) : (
        <div>
          <h2 className="text-xl font-bold mb-2">
            {searchResults.length > 0 ? 'Search Results' : 'Recent Movies'}
          </h2>

            {showAlert && (
                <Alert>
                    <p className="font-bold">No results found</p>
                </Alert>
            )}


            <div ref={movieListRef} className="flex flex-wrap justify-center -m-2"> 
                    {(searchResults.length > 0 ? searchResults : recentMovies).map(movie => (
                    <MovieCard key={movie.id} movie={movie} />
                    ))}
                </div>

          

          {/* Pagination controls */}
          {searchResults.length === 0 && !selectedMovie && (
            <div className="flex justify-between mt-4">
              <Button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
              {'<'} Prev Page
              </Button>
              <p>Page {currentPage}</p>
              <Button onClick={() => setCurrentPage(prev => prev + 1)}>
                Next Page {'>'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const MovieDetailsEndCredits = ({ endCredits, confidence }) => {
  const [showEndCredits, setShowEndCredits] = useState(false);

  // Handle null/undefined endCredits. Coerce to a string-or-null — never an object,
  // which would crash React with "Objects are not valid as a React child".
  const rawSummary = endCredits?.summary ?? (typeof endCredits === 'string' ? endCredits : null);
  const summary = typeof rawSummary === 'string' && rawSummary.trim() ? rawSummary : null;
  const actualConfidence = confidence || endCredits?.confidence || 'unknown';

  // Only show the reveal block when we actually have summary text to reveal.
  const hasSummary = !!summary && actualConfidence !== "none" && actualConfidence !== "error";

  const getConfidenceDisplay = () => {
    switch (actualConfidence) {
      case "high":
        return {
          icon: <ThumbsUp className="w-6 h-6 text-green-500 mr-2" />,
          text: "End credit scene confirmed",
          color: "text-green-500"
        };
      case "medium":
        return {
          icon: <HelpCircle className="w-6 h-6 text-blue-500 mr-2" />,
          text: "End credit scene likely",
          color: "text-blue-500"
        };
      case "low":
        return {
          icon: <AlertTriangle className="w-6 h-6 text-yellow-500 mr-2" />,
          text: "Unverified information",
          color: "text-yellow-500"
        };
      case "none":
        return {
          icon: <ThumbsDown className="w-6 h-6 text-red-500 mr-2" />,
          text: "No end credit scenes",
          color: "text-red-500"
        };
      case "error":
        return {
          icon: <AlertCircle className="w-6 h-6 text-gray-500 mr-2" />,
          text: "Unable to verify — please try again later",
          color: "text-gray-500"
        };
      default:
        return {
          icon: <HelpCircle className="w-6 h-6 text-gray-500 mr-2" />,
          text: "Unknown",
          color: "text-gray-500"
        };
    }
  };

  const display = getConfidenceDisplay();

  return (
    <div>
      <h3 className="text-xl font-semibold mb-2">End Credits Scene</h3>
      {endCredits === null ? (
        <div className="space-y-4">
          <p className="text-gray-500 italic">
            Verifying and summarizing information about the end-credit scene(s)...
          </p>
          <div className="flex items-center justify-center">
            <div
              className="w-12 h-12 border-4 rounded-full animate-spin border-t-transparent"
              style={{
                borderColor: 'hsl(30, 100%, 50%)',
                borderTopColor: 'transparent',
              }}
            ></div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center mb-2">
            {display.icon}
            <span className={`font-semibold ${display.color}`}>
              {display.text}
            </span>
          </div>

          {actualConfidence === "low" && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-3">
              <p className="text-sm text-yellow-700">
                ⚠️ This information could not be verified with official sources.
                The details below may be inaccurate or refer to a different movie.
              </p>
            </div>
          )}

          {hasSummary && (
            <p
              className={`cursor-pointer transition duration-300 ease-in-out
                ${
                  showEndCredits
                    ? 'text-[hsl(30,100%,50%)] opacity-100'
                    : 'text-white bg-[hsl(30,100%,50%)] opacity-100 hover:opacity-80'
                }
                break-words select-none p-4 rounded-md`}
              style={{ userSelect: showEndCredits ? 'text' : 'none' }}
              onClick={() => setShowEndCredits(true)}
            >
              {showEndCredits ? summary : 'Click to reveal spoilers'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};



export default MovieDatabase;